import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, CheckCircle, Wrench } from 'lucide-react';

interface ScriptWithTags {
  id: string;
  name: string;
  content: string;
  user_name?: string;
  company_name?: string;
  target_industry?: string;
  mode: string;
}

export default function ScriptMergeRepair() {
  const { toast } = useToast();
  const [scripts, setScripts] = useState<ScriptWithTags[]>([]);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const fetchScriptsWithTags = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('scripts')
        .select('id, name, content, user_name, company_name, target_industry, mode')
        .or('content.like.%{{userName}}%,content.like.%{{companyName}}%,content.like.%{{industry}}%,content.like.%{{prospect_name}}%,content.like.%{{user_name}}%,content.like.%{{company_name}}%,content.like.%{{businessType}}%');

      if (error) throw error;

      // Filter scripts that actually have merge tags and complete metadata
      const scriptsWithTags = (data || []).filter(script => {
        const hasTags = /\{\{(userName|companyName|industry|userTitle|businessType|user_name|company_name|business_type|prospect_name)\}\}/.test(script.content);
        const hasMetadata = script.user_name && script.company_name;
        return hasTags && hasMetadata;
      });

      setScripts(scriptsWithTags);
    } catch (error) {
      console.error('Error fetching scripts:', error);
      toast({
        title: "Error",
        description: "Failed to load scripts with merge tags",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const repairScript = async (script: ScriptWithTags) => {
    try {
      // Apply comprehensive merge tag resolution using script metadata
      const mergeContext = {
        userName: script.user_name,
        user_name: script.user_name,
        companyName: script.company_name,
        company_name: script.company_name,
        industry: script.target_industry,
        businessType: script.target_industry,
        business_type: script.target_industry
      };

      // Apply static merge tag resolution (comprehensive)
      let resolvedContent = script.content;
      Object.entries(mergeContext).forEach(([key, value]) => {
        if (value) {
          const pattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          resolvedContent = resolvedContent.replace(pattern, value);
        }
      });

      // Handle prospect_name specifically
      resolvedContent = resolvedContent.replace(/\{\{prospect_name\}\}/g, '[Prospect]');

      // Update the script with resolved content
      const { error } = await supabase
        .from('scripts')
        .update({ content: resolvedContent })
        .eq('id', script.id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error repairing script:', error);
      return false;
    }
  };

  const repairAllScripts = async () => {
    setRepairing(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const script of scripts) {
        const success = await repairScript(script);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      toast({
        title: "Repair Complete",
        description: `Successfully repaired ${successCount} scripts. ${failCount} failed.`,
        variant: successCount > 0 ? "default" : "destructive"
      });

      // Refresh the list
      await fetchScriptsWithTags();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to repair scripts",
        variant: "destructive"
      });
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => {
    fetchScriptsWithTags();
  }, []);

  const getTagsInScript = (content: string) => {
    const matches = content.match(/\{\{(userName|companyName|industry|userTitle|businessType|user_name|company_name|business_type|prospect_name)\}\}/g) || [];
    return [...new Set(matches)];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Script Merge Tag Repair
          </CardTitle>
          <CardDescription>
            Fix existing scripts that contain unresolved merge tags by applying their stored metadata.
            This repairs the vault system to store resolved content as designed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              ) : (
                <AlertTriangle className="h-4 w-4 text-warning" />
              )}
              <span className="text-sm">
                Found {scripts.length} scripts with unresolved merge tags
              </span>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={fetchScriptsWithTags}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button 
                onClick={repairAllScripts}
                disabled={repairing || scripts.length === 0}
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Repair All Scripts
              </Button>
            </div>
          </div>

          {scripts.length === 0 && !loading ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <p>No scripts with merge tags found. All scripts are properly resolved!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {scripts.map((script) => {
                const tags = getTagsInScript(script.content);
                return (
                  <Card key={script.id} className="border-l-4 border-l-warning">
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{script.name}</h4>
                            <Badge variant="outline">{script.mode}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <p>User: {script.user_name || 'Missing'}</p>
                            <p>Company: {script.company_name || 'Missing'}</p>
                            <p>Industry: {script.target_industry || 'Missing'}</p>
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {tags.map((tag, idx) => (
                              <Badge key={idx} variant="destructive" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => repairScript(script).then(success => {
                            if (success) {
                              toast({ title: "Success", description: `Repaired ${script.name}` });
                              fetchScriptsWithTags();
                            }
                          })}
                          disabled={repairing}
                        >
                          Repair
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}